import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  ChartBarIcon, 
  TruckIcon, 
  CubeIcon, 
  DocumentTextIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import api from '../../services/api';

interface DashboardData {
  merchant_info: {
    business_name: string;
    email: string;
    phone: string;
  };
  order_stats: {
    total_orders: number;
    active_orders: number;
    completed_orders: number;
    total_revenue: number;
    pending_revenue: number;
  };
  dispatcher_stats: {
    total_dispatchers: number;
    active_now: number;
  };
  inventory_stats: {
    total_products: number;
    low_stock_items: number;
    total_inventory_value: number;
  };
  invoice_stats: {
    unpaid_invoices: number;
    unpaid_amount: number;
  };
}

const MerchantDashboard: React.FC = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      setLoading(true);
      const response = await api.get('/merchant/dashboard');
      setDashboardData(response.data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load dashboard');
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-volexx-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-poppins">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error || !dashboardData) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <ExclamationTriangleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2 font-poppins">Error Loading Dashboard</h2>
          <p className="text-gray-600 mb-4 font-poppins">{error}</p>
          <button 
            onClick={fetchDashboard}
            className="btn-primary"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const formatCurrency = (amount: number) => {
    return `₦${amount.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 font-poppins">
            Merchant Dashboard
          </h1>
          <p className="mt-2 text-gray-600 font-poppins">
            Welcome back, {dashboardData.merchant_info.business_name || 'Merchant'}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {/* Revenue Card */}
          <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-green-100 rounded-volexx p-3">
                <CurrencyDollarIcon className="h-8 w-8 text-green-600" />
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600 font-poppins">Total Revenue</p>
                <p className="text-2xl font-bold text-green-600 font-poppins">
                  {formatCurrency(dashboardData.order_stats.total_revenue)}
                </p>
              </div>
            </div>
            <div className="pt-4 border-t border-gray-100">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 font-poppins">Pending</span>
                <span className="font-semibold text-orange-600 font-poppins">
                  {formatCurrency(dashboardData.order_stats.pending_revenue)}
                </span>
              </div>
            </div>
          </div>

          {/* Orders Card */}
          <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-volexx-blue-100 rounded-volexx p-3">
                <ChartBarIcon className="h-8 w-8 text-volexx-blue-600" />
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600 font-poppins">Total Orders</p>
                <p className="text-2xl font-bold text-volexx-blue-600 font-poppins">
                  {dashboardData.order_stats.total_orders}
                </p>
              </div>
            </div>
            <div className="pt-4 border-t border-gray-100">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 font-poppins">Active</span>
                <span className="font-semibold text-volexx-blue-600 font-poppins">
                  {dashboardData.order_stats.active_orders}
                </span>
              </div>
              <div className="flex justify-between text-sm mt-2">
                <span className="text-gray-600 font-poppins">Completed</span>
                <span className="font-semibold text-green-600 font-poppins">
                  {dashboardData.order_stats.completed_orders}
                </span>
              </div>
            </div>
          </div>

          {/* Dispatchers Card */}
          <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-orange-100 rounded-volexx p-3">
                <TruckIcon className="h-8 w-8 text-volexx-orange-600" />
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600 font-poppins">Dispatchers</p>
                <p className="text-2xl font-bold text-volexx-orange-600 font-poppins">
                  {dashboardData.dispatcher_stats.total_dispatchers}
                </p>
              </div>
            </div>
            <div className="pt-4 border-t border-gray-100">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 font-poppins">Active Now</span>
                <span className="font-semibold text-green-600 font-poppins">
                  {dashboardData.dispatcher_stats.active_now}
                </span>
              </div>
            </div>
          </div>

          {/* Inventory Card */}
          <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-purple-100 rounded-volexx p-3">
                <CubeIcon className="h-8 w-8 text-purple-600" />
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600 font-poppins">Products</p>
                <p className="text-2xl font-bold text-purple-600 font-poppins">
                  {dashboardData.inventory_stats.total_products}
                </p>
              </div>
            </div>
            <div className="pt-4 border-t border-gray-100">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 font-poppins">Low Stock</span>
                <span className={`font-semibold font-poppins ${
                  dashboardData.inventory_stats.low_stock_items > 0 ? 'text-red-600' : 'text-green-600'
                }`}>
                  {dashboardData.inventory_stats.low_stock_items}
                </span>
              </div>
              <div className="flex justify-between text-sm mt-2">
                <span className="text-gray-600 font-poppins">Value</span>
                <span className="font-semibold text-purple-600 font-poppins">
                  {formatCurrency(dashboardData.inventory_stats.total_inventory_value)}
                </span>
              </div>
            </div>
          </div>

          {/* Invoices Card */}
          <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="bg-yellow-100 rounded-volexx p-3">
                <DocumentTextIcon className="h-8 w-8 text-yellow-600" />
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600 font-poppins">Unpaid Invoices</p>
                <p className="text-2xl font-bold text-yellow-600 font-poppins">
                  {dashboardData.invoice_stats.unpaid_invoices}
                </p>
              </div>
            </div>
            <div className="pt-4 border-t border-gray-100">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 font-poppins">Amount Due</span>
                <span className="font-semibold text-yellow-600 font-poppins">
                  {formatCurrency(dashboardData.invoice_stats.unpaid_amount)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-volexx-lg shadow-volexx border border-gray-100 p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4 font-poppins">Quick Actions</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <button className="flex flex-col items-center p-4 border border-gray-200 rounded-volexx hover:border-volexx-blue-500 hover:bg-volexx-blue-50 transition-colors">
              <ChartBarIcon className="h-8 w-8 text-volexx-blue-600 mb-2" />
              <span className="text-sm font-medium text-gray-900 font-poppins">Create Order</span>
            </button>
            <button className="flex flex-col items-center p-4 border border-gray-200 rounded-volexx hover:border-volexx-orange-500 hover:bg-orange-50 transition-colors">
              <CubeIcon className="h-8 w-8 text-volexx-orange-600 mb-2" />
              <span className="text-sm font-medium text-gray-900 font-poppins">Add Product</span>
            </button>
            <button className="flex flex-col items-center p-4 border border-gray-200 rounded-volexx hover:border-yellow-500 hover:bg-yellow-50 transition-colors">
              <DocumentTextIcon className="h-8 w-8 text-yellow-600 mb-2" />
              <span className="text-sm font-medium text-gray-900 font-poppins">New Invoice</span>
            </button>
            <button className="flex flex-col items-center p-4 border border-gray-200 rounded-volexx hover:border-green-500 hover:bg-green-50 transition-colors">
              <TruckIcon className="h-8 w-8 text-green-600 mb-2" />
              <span className="text-sm font-medium text-gray-900 font-poppins">View Dispatchers</span>
            </button>
          </div>
        </div>

        {/* Alerts */}
        {dashboardData.inventory_stats.low_stock_items > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-volexx-lg p-4 mb-8">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600 mr-3 mt-0.5" />
              <div>
                <h3 className="text-sm font-semibold text-yellow-800 font-poppins">
                  Low Stock Alert
                </h3>
                <p className="text-sm text-yellow-700 mt-1 font-poppins">
                  You have {dashboardData.inventory_stats.low_stock_items} products running low on stock. 
                  <a href="/merchant/inventory" className="font-semibold underline ml-1">
                    View inventory
                  </a>
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MerchantDashboard;
